// ============================================================
// Platform Control · API Keys — key management surface.
// Real: list, issue, rotate, revoke. All OLIVIUM-audited.
// ============================================================

import { useCallback, useEffect, useState } from 'react'
import ViewShell, { Card, Row, Section, ActionButton, ActionRow } from './_ViewShell'
import EmptyState from '../../../components/EmptyState'
import { SHELL_TOKENS, text } from '../../../shell/styles'
import { api, isAuthError } from '../../../api/client'

interface ApiKey {
  id: string
  label: string
  prefix: string
  tier: string
  status: string
  created_at: string
  revoked_at: string | null
}

export default function ApiKeysView() {
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [loading, setLoading] = useState(true)
  const [rotating, setRotating] = useState(false)
  const [rotateResult, setRotateResult] = useState<string | null>(null)
  const [issuing, setIssuing] = useState(false)
  const [issuedPlaintext, setIssuedPlaintext] = useState<string | null>(null)

  const fetchKeys = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.admin.keys.list()
      setKeys((r.keys || []) as ApiKey[])
    } catch (err) {
      if (!isAuthError(err)) console.warn('[ApiKeys]', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void fetchKeys() }, [fetchKeys])

  async function handleRotate() {
    if (rotating) return
    const reason = window.prompt('Rotation reason (required):')
    if (!reason) return
    setRotating(true)
    setRotateResult(null)
    try {
      const result = await api.admin.rotateKeys(reason)
      setRotateResult(`Keys rotated successfully. New prefix: ${result.newPublicKeyPrefix}`)
      void fetchKeys()
    } catch (err) {
      setRotateResult(`Rotation failed: ${(err as Error).message}`)
    } finally {
      setRotating(false)
    }
  }

  async function handleIssue() {
    const label = window.prompt('Key label (e.g. "Production API"):')
    if (!label) return
    setIssuing(true)
    try {
      const r = await api.admin.keys.issue({ label })
      setIssuedPlaintext(r.plaintext)
      void fetchKeys()
    } catch (err) {
      alert(`Issue failed: ${(err as Error).message}`)
    } finally {
      setIssuing(false)
    }
  }

  async function handleRevoke(keyId: string) {
    const reason = window.prompt('Revocation reason:')
    if (!reason) return
    try {
      await api.admin.keys.revoke(keyId, reason)
      void fetchKeys()
    } catch (err) {
      alert(`Revoke failed: ${(err as Error).message}`)
    }
  }

  const activeKeys = keys.filter((k) => k.status === 'active')
  const revokedKeys = keys.filter((k) => k.status === 'revoked')

  return (
    <ViewShell
      kicker="PLATFORM CONTROL · API KEYS"
      title="API Keys"
      description="Issue, rotate, and revoke API keys. Every operation is recorded in OLIVIUM."
    >
      {/* Actions */}
      <Section label="ACTIONS">
        <ActionRow>
          <ActionButton variant="primary" onClick={handleRotate} disabled={rotating}>
            {rotating ? 'Rotating...' : 'Rotate All Keys'}
          </ActionButton>
          <ActionButton onClick={handleIssue} disabled={issuing}>
            {issuing ? 'Issuing...' : 'Issue New Key'}
          </ActionButton>
        </ActionRow>
      </Section>

      {/* Rotation result */}
      {rotateResult && (
        <Section label="RESULT">
          <div style={{
            padding: '12px 16px',
            borderRadius: 10,
            background: rotateResult.includes('failed') ? 'rgba(220,38,38,0.06)' : 'rgba(5,150,105,0.06)',
            border: `1px solid ${rotateResult.includes('failed') ? 'rgba(220,38,38,0.2)' : 'rgba(5,150,105,0.2)'}`,
            fontSize: 12,
            fontFamily: 'var(--font-mono)',
            color: rotateResult.includes('failed') ? '#f87171' : '#34d399',
          }}>
            {rotateResult}
          </div>
        </Section>
      )}

      {/* Issued plaintext warning */}
      {issuedPlaintext && (
        <Section label="NEW KEY — COPY NOW">
          <div style={{
            padding: '16px',
            borderRadius: 10,
            background: 'rgba(245,158,11,0.06)',
            border: '1px solid rgba(245,158,11,0.25)',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#fbbf24', marginBottom: 8 }}>
              This key will only be shown once. Copy it now.
            </div>
            <div style={{
              fontSize: 13,
              fontFamily: 'var(--font-mono)',
              color: SHELL_TOKENS.ink,
              background: 'rgba(255,255,255,0.04)',
              padding: '10px 14px',
              borderRadius: 8,
              wordBreak: 'break-all',
              userSelect: 'all',
              marginBottom: 10,
            }}>
              {issuedPlaintext}
            </div>
            <ActionButton onClick={() => {
              navigator.clipboard.writeText(issuedPlaintext).catch(() => {})
              setIssuedPlaintext(null)
            }}>
              Copy and Dismiss
            </ActionButton>
          </div>
        </Section>
      )}

      {/* Key list */}
      <Section label={`ACTIVE KEYS (${activeKeys.length})`}>
        {loading ? (
          <div style={{ padding: 16, color: SHELL_TOKENS.inkFaint, fontFamily: 'var(--font-mono)', fontSize: 12 }}>
            Loading keys...
          </div>
        ) : activeKeys.length === 0 ? (
          <EmptyState
            title="No active keys"
            body="Issue a key using the button above to get started."
          />
        ) : (
          <Card>
            {activeKeys.map((k) => (
              <div key={k.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 0', borderBottom: `1px solid ${SHELL_TOKENS.border}`,
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: SHELL_TOKENS.ink }}>
                    {k.label || k.tier}
                  </div>
                  <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.inkMuted, marginTop: 3 }}>
                    {k.prefix}... · {k.tier}
                  </div>
                </div>
                <ActionButton variant="danger" onClick={() => handleRevoke(k.id)}>
                  Revoke
                </ActionButton>
              </div>
            ))}
          </Card>
        )}
      </Section>

      {revokedKeys.length > 0 && (
        <Section label={`REVOKED (${revokedKeys.length})`}>
          <Card>
            {revokedKeys.map((k) => (
              <div key={k.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 0', borderBottom: `1px solid ${SHELL_TOKENS.border}`,
                opacity: 0.5,
              }}>
                <div>
                  <div style={{ fontSize: 12, color: SHELL_TOKENS.inkDim }}>{k.label || k.tier}</div>
                  <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.inkFaint }}>
                    {k.prefix}... · revoked
                  </div>
                </div>
                <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.red }}>REVOKED</span>
              </div>
            ))}
          </Card>
        </Section>
      )}

      {/* Policy */}
      <Section label="POLICY">
        <Card>
          <Row label="Rotation" value="Admin-only" hint="Requires operator level 80+, audited as critical" />
          <Row label="Rate limit" value="5 rotations/hour" hint="Enforced server-side" />
          <Row label="Plaintext" value="Shown once at issue" hint="Never stored or retrievable after creation" />
          <Row label="Audit" value="Every operation recorded" hint="OLIVIUM audit trail with severity: critical" />
        </Card>
      </Section>
    </ViewShell>
  )
}
